import React from 'react';
import { Globe, Linkedin, Instagram, Twitter, Facebook } from 'lucide-react';

/**
 * SocialShortcuts - Display social media and website icons that link to URLs
 * Icons only appear if the corresponding URL exists
 * @param {Object} props
 * @param {string} props.website - Website URL (Company only)
 * @param {string} props.linkedin_url - LinkedIn URL
 * @param {string} props.instagram_url - Instagram URL
 * @param {string} props.twitter_url - Twitter/X URL
 * @param {string} props.facebook_url - Facebook URL
 * @param {string} props.tiktok_url - TikTok URL
 * @param {boolean} props.showWebsite - Whether to show website icon (default: true)
 */
function SocialShortcuts({
  website,
  linkedin_url,
  instagram_url,
  twitter_url,
  facebook_url,
  tiktok_url,
  showWebsite = true,
}) {
  const links = [];

  // Add website (Company only)
  if (showWebsite && website) {
    links.push({
      url: website,
      icon: Globe,
      label: 'Website',
      color: 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200',
    });
  }

  // Add LinkedIn
  if (linkedin_url) {
    links.push({
      url: linkedin_url,
      icon: Linkedin,
      label: 'LinkedIn',
      color: 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300',
    });
  }

  // Add Instagram
  if (instagram_url) {
    links.push({
      url: instagram_url,
      icon: Instagram,
      label: 'Instagram',
      color: 'text-pink-600 hover:text-pink-800 dark:text-pink-400 dark:hover:text-pink-300',
    });
  }

  // Add Twitter/X
  if (twitter_url) {
    links.push({
      url: twitter_url,
      icon: Twitter,
      label: 'Twitter',
      color: 'text-sky-600 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300',
    });
  }

  // Add Facebook
  if (facebook_url) {
    links.push({
      url: facebook_url,
      icon: Facebook,
      label: 'Facebook',
      color: 'text-blue-700 hover:text-blue-900 dark:text-blue-500 dark:hover:text-blue-400',
    });
  }

  // Add TikTok (using a custom SVG since lucide doesn't have TikTok)
  if (tiktok_url) {
    links.push({
      url: tiktok_url,
      customIcon: (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-5 h-5"
          aria-label="TikTok"
        >
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
        </svg>
      ),
      label: 'TikTok',
      color: 'text-gray-900 hover:text-black dark:text-gray-300 dark:hover:text-white',
    });
  }

  // Don't render anything if there are no links
  if (links.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center space-x-2 mt-2">
      {links.map((link, index) => {
        const Icon = link.icon;
        return (
          <a
            key={index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${link.color} transition-colors`}
            title={link.label}
            aria-label={link.label}
          >
            {link.customIcon || <Icon className="w-5 h-5" />}
          </a>
        );
      })}
    </div>
  );
}

export default SocialShortcuts;
